import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, X, AlertTriangle, Ruler, LayoutGrid } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";

const HEIGHT_BANDS = [
  { key: "below_9",  label: 'Below 9"',  pct: 0,   tag: "No fee" },
  { key: "9_to_15",  label: '9 – 15"',   pct: 100, tag: "+100%" },
  { key: "16_to_21", label: '16 – 21"',  pct: 300, tag: "+300%" },
  { key: "above_22", label: 'Above 22"', pct: 450, tag: "+450%" },
];
const AREA_OPTIONS = [
  { key: "partial", label: "Partial area",  hint: "half of property" },
  { key: "full",    label: "Full property", hint: "every section overgrown" },
];

export default function LongGrassFeeDialog({ open, onClose, booking, onSaved }) {
  const [height, setHeight] = useState("9_to_15");
  const [area, setArea] = useState("full");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const original = useMemo(
    () => Number(booking?.primary_price ?? booking?.price ?? 0),
    [booking]
  );
  const band = HEIGHT_BANDS.find((b) => b.key === height) || HEIGHT_BANDS[1];
  const pct = area === "partial" ? band.pct * 0.5 : band.pct;
  const amount = Math.round((original * pct) / 100 * 100) / 100;
  const total = Math.round((original + amount) * 100) / 100;
  const isZero = amount <= 0;

  const submit = async () => {
    if (!booking?.booking_id || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/bookings/${booking.booking_id}/long-grass-fee`,
        { height_band: height, area_scope: area, note: note || null }
      );
      if (data.cleared) {
        toast.success("No surcharge applied — fee cleared.");
      } else {
        toast.success(`Long grass fee added: $${(data.long_grass_fee?.amount || amount).toFixed(2)}`);
      }
      onSaved?.(data.booking || null);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save fee");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/55 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="lg-fee-dialog"
    >
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-[#7c2d12] via-[#9a3412] to-[#7c2d12] text-white px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-amber-100">Long grass detected</div>
            <div className="text-lg font-extrabold tracking-tight font-[Outfit] truncate">{booking?.service_name || "Lawn mowing"}</div>
            <div className="text-[12px] text-amber-100/85 truncate">Original mow ${original.toFixed(2)} · #{booking?.booking_id?.slice(-6)}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Height bands */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
              <Ruler className="w-3.5 h-3.5" /> Grass height
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {HEIGHT_BANDS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setHeight(b.key)}
                  data-testid={`lg-height-${b.key}`}
                  className={`text-left rounded-2xl border-2 p-3 transition active:scale-[0.98] ${height === b.key ? "border-[#7c2d12] bg-amber-50" : "border-gray-200 bg-white hover:border-amber-300"}`}
                >
                  <div className="text-sm font-extrabold tracking-tight text-gray-900">{b.label}</div>
                  <div className={`mt-1 text-[10px] uppercase tracking-[0.14em] font-extrabold ${b.pct === 0 ? "text-emerald-700" : "text-[#7c2d12]"}`}>{b.tag}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Affected area */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
              <LayoutGrid className="w-3.5 h-3.5" /> Affected area
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {AREA_OPTIONS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setArea(a.key)}
                  data-testid={`lg-area-${a.key}`}
                  className={`text-left rounded-2xl border-2 p-3 transition active:scale-[0.98] ${area === a.key ? "border-[#7c2d12] bg-amber-50" : "border-gray-200 bg-white hover:border-amber-300"}`}
                >
                  <div className="text-sm font-extrabold tracking-tight text-gray-900">{a.label}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">{a.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Live amount preview */}
          <div className={`rounded-2xl p-4 ${isZero ? "bg-emerald-50 border-2 border-emerald-200" : "bg-gradient-to-br from-[#7c2d12] to-[#451a03] text-white"}`} data-testid="lg-preview">
            <div className={`text-[10px] uppercase tracking-[0.18em] font-extrabold ${isZero ? "text-emerald-700" : "text-amber-200"}`}>
              {isZero ? "No surcharge" : "Surcharge preview"}
            </div>
            {isZero ? (
              <div className="mt-1 text-sm text-emerald-800">Grass is within normal range — no extra charge will be applied.</div>
            ) : (
              <>
                <div className="mt-1 grid grid-cols-3 gap-2 text-[11px]">
                  <Cell label="Original" value={`$${original.toFixed(2)}`} />
                  <Cell label="LG fee" value={`+$${amount.toFixed(2)}`} highlight />
                  <Cell label="Total due" value={`$${total.toFixed(2)}`} />
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.14em] font-extrabold text-amber-200/90">
                  {band.label} · {area === "partial" ? "Partial" : "Full"} · {Math.round(pct)}% over original
                </div>
              </>
            )}
          </div>

          {/* Crew note */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Crew note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Back yard was waist-high — needed two passes"
              rows={2}
              className="mt-1.5"
              data-testid="lg-note"
            />
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className={`rounded-full h-11 px-5 font-extrabold ${isZero ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#7c2d12] hover:bg-[#9a3412] text-white"}`}
            data-testid="lg-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isZero ? "Clear fee" : `Send fee · $${amount.toFixed(2)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-2 py-1.5 ${highlight ? "bg-white/15" : "bg-white/10"}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-extrabold text-amber-200/80">{label}</div>
      <div className={`mt-0.5 font-extrabold tabular-nums ${highlight ? "text-amber-100" : "text-white"}`}>{value}</div>
    </div>
  );
}
